import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormNode } from '@/utils/api/aboboApiTypes';
import BaseQuestionType from './BaseQuestionType';
import { useFormWizard } from '@/contexts/FormWizardContext';

// Import shadcn components
import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, VStack } from '@/components/ui';
import QuestionHeading from '@/components/QuestionHeading';
import QuestionHelperText from '@/components/QuestionHelperText';

interface AddressProps {
  node: FormNode;
}

interface AddressData {
  street: string;
  apt?: string;
  city: string;
  state: string;
  zipCode: string;
}

// US States for the dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export default forwardRef<any, AddressProps>(function Address(
  { node },
  ref
) {
  const { t } = useTranslation();
  const { form } = useFormWizard();
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const validateAddress = (values: any[]) => {
    // Don't validate until user has attempted to submit or blurred a field
    if (!hasAttemptedSubmit && !form.formState.isSubmitted) {
      return true;
    }

    const addressData = values?.[0]?.data;

    if (node.metadata?.required) {
      if (!addressData) {
        return t('questionTypes.address.validation.required');
      }

      // Parse the JSON data if it exists
      try {
        const parsed: AddressData = JSON.parse(addressData);

        if (!parsed.street?.trim()) {
          return t('questionTypes.address.validation.streetRequired');
        }

        if (!parsed.city?.trim()) {
          return t('questionTypes.address.validation.cityRequired');
        }

        if (!parsed.state?.trim()) {
          return t('questionTypes.address.validation.stateRequired');
        }

        if (!parsed.zipCode?.trim()) {
          return t('questionTypes.address.validation.zipRequired');
        }

        // Basic ZIP code validation (5 digits or 5+4 format)
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!zipRegex.test(parsed.zipCode)) {
          return t('questionTypes.address.validation.zipInvalid');
        }
      } catch {
        return t('questionTypes.address.validation.invalid');
      }
    }

    return true;
  };

  const transformInput = (values: any[]) => {
    const addressData = values?.[0]?.data;
    if (!addressData) {
      return { street: '', apt: '', city: '', state: '', zipCode: '' };
    }
    
    try {
      return JSON.parse(addressData) as AddressData;
    } catch {
      return { street: '', apt: '', city: '', state: '', zipCode: '' };
    }
  };

  const transformOutput = (addressData: AddressData) => {
    // Always return the data, even if empty, to maintain form state
    return [{ data: JSON.stringify(addressData) }];
  };

  return (
    <BaseQuestionType
      node={node}
      validate={validateAddress}
      transform={{
        input: transformInput,
        output: transformOutput,
      }}
    >
      {(field) => {
        // Use the transformed field value directly
        const addressData = field.value || { street: '', apt: '', city: '', state: '', zipCode: '' };

        const handleAddressChange = (addressField: keyof AddressData, value: string) => {
          const newAddress = { ...addressData, [addressField]: value };
          field.onChange(newAddress);
        };

        const handleInputBlur = () => {
          setHasAttemptedSubmit(true);
        };

        return (
          <div className="space-y-4" ref={ref}>
            <QuestionHeading required={node.metadata.required}>{node.body}</QuestionHeading>

            {node.helperText && (
              <QuestionHelperText>
                {node.helperText}
              </QuestionHelperText>
            )}

            <VStack className="space-y-4 w-full">
              <div className="w-full">
                <Label htmlFor="street" className="text-sm font-medium">
                  {t('questionTypes.address.labels.street')} {node.metadata.required && '*'}
                </Label>
                <Input
                  id="street"
                  type="text"
                  autoComplete="off"
                  placeholder="123 Main St"
                  value={addressData.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  onBlur={handleInputBlur}
                  className="w-full"
                />
              </div>

              <div className="w-full">
                <Label htmlFor="apt" className="text-sm font-medium">
                  {t('questionTypes.address.labels.apt')}
                </Label>
                <Input
                  id="apt"
                  type="text"
                  autoComplete="off"
                  placeholder="Apt 4B"
                  value={addressData.apt || ''}
                  onChange={(e) => handleAddressChange('apt', e.target.value)}
                  onBlur={handleInputBlur}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                <div>
                  <Label htmlFor="city" className="text-sm font-medium">
                    {t('questionTypes.address.labels.city')} {node.metadata.required && '*'}
                  </Label>
                  <Input
                    id="city"
                    type="text"
                    autoComplete="off"
                    placeholder="New York"
                    value={addressData.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    onBlur={handleInputBlur}
                    className="w-full"
                  />
                </div>

                <div>
                  <Label htmlFor="state" className="text-sm font-medium">
                    {t('questionTypes.address.labels.state')} {node.metadata.required && '*'}
                  </Label>
                  <Select
                    value={addressData.state}
                    onValueChange={(value) => handleAddressChange('state', value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('questionTypes.address.labels.selectState')} />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="zipCode" className="text-sm font-medium">
                    {t('questionTypes.address.labels.zipCode')} {node.metadata.required && '*'}
                  </Label>
                  <Input
                    id="zipCode"
                    type="text"
                    autoComplete="off"
                    placeholder="12345"
                    value={addressData.zipCode}
                    onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                    onBlur={handleInputBlur}
                    className="w-full"
                    maxLength={10}
                  />
                </div>
              </div>
            </VStack>

            {/* Only show validation errors when form has been submitted or user tried to navigate */}
            {field.error && (form.formState.isSubmitted || hasAttemptedSubmit) && (
              <p className="text-red-500 text-sm text-center">{field.error}</p>
            )}
          </div>
        );
      }}
    </BaseQuestionType>
  );
});